import type { FlowAPI } from '@wix/yoshi-flow-editor';
import {
  type IWidgetControllerConfig,
  type IWixWindow,
  type ISiteApis,
  type ILocation,
  type IUserApis,
  type ISeo,
} from '@wix/native-components-infra/dist/src/types/types';

const APIS_TO_LOAD = ['location', 'seo', 'site', 'user', 'window'] as const;
const APIS_TO_LOAD_COMMENTS = [
  'location',
  'seo',
  'site',
  'user',
  'window',
  'authentication',
] as const;

type AuthenticationApi = Record<string, unknown>;

/**
 * Replacement for old wixCodeApi, represents the APIs that are loaded from the platform
 *
 * IWixAPI will have a leaner api when $.appFields.platform.migratedToNewPlatformApi == true
 * Missing APIs will need to be loaded lazily via `getPlatformApi` method.
 **/
export type PlatformApi = {
  [key in (typeof APIS_TO_LOAD | typeof APIS_TO_LOAD_COMMENTS)[number]]: {
    location: ILocation;
    seo: ISeo;
    site: ISiteApis;
    user: IUserApis;
    window: IWixWindow;
    authentication: AuthenticationApi;
  }[key];
};

const HANG_WARNING_MS = 3000;
const INTERACTION_NAME = 'blog-platform-api-load';

export const getPlatformApi = async (
  platformApiProvider: IWidgetControllerConfig['platformApiProvider'],
  comments: boolean = false,
  panoramaClient?: FlowAPI['panoramaClient'],
): Promise<PlatformApi> => {
  const entries = comments ? APIS_TO_LOAD_COMMENTS : APIS_TO_LOAD;
  const resolved = new Set<string>();
  const tx = panoramaClient?.transaction(INTERACTION_NAME);

  tx?.start();

  const entriesToApisPromises = entries.map((loadedApiKey) => {
    // Wrapping with Promise.resolve, since getPlatformAPI returns a string in integration test env
    const wrappedApi = Promise.resolve(platformApiProvider.getPlatformApi(loadedApiKey));

    return wrappedApi.then((api) => {
      resolved.add(loadedApiKey);
      return [loadedApiKey, api];
    });
  });

  const hangWarning = tx
    ? setTimeout(() => {
        const pending = entries.filter((key) => !resolved.has(key));
        panoramaClient
          ?.logger()
          .warn(`[Blog] ${INTERACTION_NAME} still pending after ${HANG_WARNING_MS}ms`, {
            pending,
            resolved: [...resolved],
          });
      }, HANG_WARNING_MS)
    : undefined;

  try {
    const result = Object.fromEntries(await Promise.all(entriesToApisPromises)) as PlatformApi;
    clearTimeout(hangWarning);
    tx?.finish();
    return result;
  } catch (err) {
    clearTimeout(hangWarning);
    const error = err instanceof Error ? err : new Error(String(err));
    panoramaClient?.errorMonitor().reportError(error, { tags: { interaction: INTERACTION_NAME } });
    throw err;
  }
};
